import type { ComponentProps } from 'react';
import type { ActionConfirmDialog } from '../../../components/common/ActionConfirmDialog';
import { escalationLabel } from './rowStateLabels';

/** Suppress / Delete / bulk Remove / Move to brand dashboard share one confirmation flow (Task 23). */
export type PendingConfirm =
  | { kind: 'remove'; entryIds: string[]; name?: string; skipped?: number }
  | { kind: 'suppress'; entryId: string; email: string }
  | { kind: 'discardDraft'; entryId: string; messageId: number; name?: string }
  | { kind: 'notUnsubscribe'; entryId: string; name?: string; mode: 'reopen' | 'lift_only' }
  | { kind: 'moveToBrand'; entryIds: string[]; name?: string; rate?: string; flag?: { reason?: string } };

type Copy = Pick<ComponentProps<typeof ActionConfirmDialog>, 'variant' | 'title' | 'description' | 'confirmLabel'>;

const EMPTY: Copy = { variant: 'change', title: '', description: '', confirmLabel: 'Confirm' };

const skippedNote = (n = 0): string =>
  n > 0 ? ` ${n} selected ${n === 1 ? 'creator' : 'creators'} on other pages or already with the brand ${n === 1 ? 'was' : 'were'} not included.` : '';

export function pendingConfirmCopy(pending: PendingConfirm | null): Copy {
  if (!pending) return EMPTY;
  if (pending.kind === 'suppress') {
    return { variant: 'reject', title: `Suppress ${pending.email}?`, description: "We'll never email this address again from any campaign. Queued sends are cancelled.", confirmLabel: 'Suppress' };
  }
  if (pending.kind === 'discardDraft') {
    return {
      variant: 'delete',
      title: 'Discard draft?',
      description: `${pending.name ?? 'This creator'}'s first-touch draft is deleted and nothing is sent. The Bulk send wizard writes a fresh one next time.`,
      confirmLabel: 'Discard draft',
    };
  }
  if (pending.kind === 'notUnsubscribe') {
    const who = pending.name ?? 'This creator';
    if (pending.mode === 'lift_only') {
      return {
        variant: 'change',
        title: 'Not an unsubscribe?',
        description: `${who} is with the brand, so the conversation stays where it is. This lifts the do-not-contact block the agent set from their reply and clears Needs you.`,
        confirmLabel: 'Lift the block',
      };
    }
    return {
      variant: 'change',
      title: 'Not an unsubscribe?',
      description: `The agent read ${who}'s reply as an unsubscribe. This lifts that do-not-contact block, reopens the conversation and opens the reply box. It is recorded in the audit log.`,
      confirmLabel: 'Lift and reopen',
    };
  }
  if (pending.kind === 'remove') {
    const single = pending.entryIds.length === 1;
    return {
      variant: single ? 'delete' : 'remove',
      title: single ? 'Remove from campaign?' : `Remove ${pending.entryIds.length} creators?`,
      description: `${pending.name ?? `${pending.entryIds.length} creators`} ${single ? 'is' : 'are'} removed from this campaign's outreach and nothing more is sent. Sent messages stay in the thread. Reopen on the Removed tab brings them back.${skippedNote(pending.skipped)}`,
      confirmLabel: 'Remove',
    };
  }
  if (pending.entryIds.length === 1) {
    const moved = `${pending.name} goes to the brand's Creators → Pending at the agreed rate${pending.rate ? ` (${pending.rate})` : ''}. The thread stays here.`;
    if (pending.flag) {
      const why = pending.flag.reason ? `Needs you · ${escalationLabel(pending.flag.reason)}` : 'Needs you';
      return {
        variant: 'change',
        title: 'This conversation still needs you',
        description: `${why}. Moving it to the brand clears that flag. ${moved}`,
        confirmLabel: 'Move and clear the flag',
      };
    }
    return { variant: 'change', title: 'Move to brand dashboard?', description: moved, confirmLabel: 'Move' };
  }
  return {
    variant: 'change',
    title: `Move ${pending.entryIds.length} creators to the brand dashboard?`,
    description: "Each creator with an agreed rate goes to the brand's Creators → Pending at that rate. Anyone without an agreed rate, anyone who still needs you, and creators already with the brand, are skipped. The threads stay here.",
    confirmLabel: 'Move',
  };
}
